import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { konsollApi, type Hendelse, type ReiseDetaljer } from '../../api/konsoll';
import type { Sporsmal, SvarInn } from '../../api/reise';
import SporsmalSkjema from '../reise/SporsmalSkjema';
import { datoTid } from './ReiseListe';

interface Props {
  reiseId: string;
  detaljer: ReiseDetaljer;
  onEndret: () => void;
}

/** Verdien slik den er lagret: koder og tall. De ansatte kjenner kodene fra innholdet. */
function visVerdi(verdi: unknown): string {
  if (verdi === null || verdi === undefined) return '';
  if (typeof verdi === 'string' || typeof verdi === 'number') return String(verdi);
  if (Array.isArray(verdi) && verdi.every((v) => typeof v === 'string')) return verdi.join(', ');
  return JSON.stringify(verdi);
}

/**
 * Svarene per modul, med kilde, sikkerhet, hvem, når og hvorfor. Motsigelser står øverst, og spørsmål som ikke
 * er besvart, står i modulen. Mens reisen er aktiv, kan de ansatte rette og berike.
 */
export default function SvarPanel({ reiseId, detaljer, onEndret }: Props) {
  const { t, i18n } = useTranslation();
  const [apen, setApen] = useState<{ sporsmalId: string; instans: string } | null>(null);
  const [lagret, setLagret] = useState(false);
  const kanRette = detaljer.reise.tilstand === 'aktiv';

  const erApen = (sporsmalId: string, instans: string) => apen?.sporsmalId === sporsmalId && apen.instans === instans;
  const apne = (sporsmalId: string, instans: string) => {
    setLagret(false);
    setApen({ sporsmalId, instans });
  };

  return (
    <div>
      {lagret && (
        <p role="status" className="mb-4 melding-ok">
          {t('konsoll.rett.lagret')}
        </p>
      )}

      {detaljer.motsigelser.length > 0 && (
        <div className="mb-4 melding-feil">
          <h3 className="mb-2 flex items-center gap-2 font-semibold">
            <AlertTriangle aria-hidden="true" className="h-5 w-5" />
            {t('konsoll.detalj.motsigelser')}
          </h3>
          <ul className="list-inside list-disc">
            {detaljer.motsigelser.map((m) => (
              <li key={`${m.modulId}.${m.id}`}>
                {m.tekst} <span className="text-sm">({m.sporsmalIder.join(', ')})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {detaljer.fremdrift.moduler.map((modul) => {
        const svar = detaljer.svar.filter((s) => s.modulId === modul.modulId);
        const ubesvarte = detaljer.ubesvarte.filter((u) => u.modulId === modul.modulId);
        if (svar.length === 0 && ubesvarte.length === 0) return null;
        return (
          <section key={modul.modulId} aria-labelledby={`modul-${modul.modulId}`} className="mb-6">
            <h3 id={`modul-${modul.modulId}`} className="mb-2 font-semibold">
              {modul.tittel} <span className="text-sm font-normal text-slate-600">· {t('konsoll.detalj.besvart', { besvart: modul.besvart, synlige: modul.synlige })}</span>
            </h3>
            <ul className="flex flex-col gap-2">
              {svar.map((s) => (
                <li key={s.id} className={'rounded-lg border bg-white p-4 ' + (s.status === 'gjeldende' ? 'border-slate-200' : 'border-amber-400')}>
                  <Svar svar={s} sprak={i18n.resolvedLanguage} />
                  {kanRette && s.status !== 'gjelder_ikke_lenger' && !erApen(s.sporsmalId, s.instans) && (
                    <button
                      type="button"
                      onClick={() => apne(s.sporsmalId, s.instans)}
                      aria-label={t('konsoll.svar.rettSvar', { sporsmal: s.sporsmalTekst ?? s.sporsmalId })}
                      className="knapp-sekundaer mt-2"
                    >
                      {t('konsoll.svar.rett')}
                    </button>
                  )}
                  {erApen(s.sporsmalId, s.instans) && (
                    <Rettelse
                      reiseId={reiseId}
                      sporsmalId={s.sporsmalId}
                      instans={s.instans}
                      onLagret={() => {
                        setApen(null);
                        setLagret(true);
                        onEndret();
                      }}
                      onAvbryt={() => setApen(null)}
                    />
                  )}
                </li>
              ))}
            </ul>
            {ubesvarte.length > 0 && (
              <div className="mt-3">
                <h4 className="mb-1 text-sm font-semibold text-slate-700">{t('konsoll.detalj.ubesvarte')}</h4>
                <ul className="flex flex-col gap-2">
                  {ubesvarte.map((u) => (
                    <li key={`${u.sporsmalId}|${u.instans}`} className="rounded-lg border border-dashed border-slate-300 bg-white p-4">
                      <p>
                        {u.tekst} {u.profil && <span className="text-sm text-slate-600">({u.profil})</span>}
                      </p>
                      {kanRette && !erApen(u.sporsmalId, u.instans) && (
                        <button
                          type="button"
                          onClick={() => apne(u.sporsmalId, u.instans)}
                          aria-label={t('konsoll.svar.berikSvar', { sporsmal: u.tekst })}
                          className="knapp-sekundaer mt-2"
                        >
                          {t('konsoll.svar.berik')}
                        </button>
                      )}
                      {erApen(u.sporsmalId, u.instans) && (
                        <Rettelse
                          reiseId={reiseId}
                          sporsmalId={u.sporsmalId}
                          instans={u.instans}
                          onLagret={() => {
                            setApen(null);
                            setLagret(true);
                            onEndret();
                          }}
                          onAvbryt={() => setApen(null)}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function Svar({ svar: s, sprak }: { svar: Hendelse; sprak?: string }) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0">
      <p className="font-medium">
        {s.sporsmalTekst ?? s.sporsmalId}
        {s.instans && <span className="font-normal text-slate-600"> ({s.instans})</span>}
      </p>
      <p className="break-words text-slate-800">{s.verdi === null ? t('konsoll.svar.tom') : visVerdi(s.verdi)}</p>
      {s.kundensOrd && <p className="break-words italic text-slate-700">«{s.kundensOrd}»</p>}
      <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-600">
        {s.status !== 'gjeldende' && <span className="font-medium text-amber-900">{t(`konsoll.svar.status.${s.status}`)}</span>}
        {s.vetIkke && <span>{t('konsoll.svar.standardvalg')}</span>}
        <span>{t('konsoll.svar.kilde', { kilde: t(`konsoll.kilde.${s.kilde}`, { defaultValue: s.kilde }) })}</span>
        <span>{t('konsoll.svar.sikkerhet', { sikkerhet: t(`konsoll.sikkerhet.${s.sikkerhet}`, { defaultValue: s.sikkerhet }) })}</span>
        <span>{t('konsoll.svar.av', { av: s.av, tid: datoTid(s.tidspunkt, sprak) })}</span>
      </p>
      {s.begrunnelse && <p className="mt-1 text-sm text-slate-700">{t('konsoll.svar.begrunnelse', { tekst: s.begrunnelse })}</p>}
    </div>
  );
}

interface RettelseProps {
  reiseId: string;
  sporsmalId: string;
  instans: string;
  onLagret: () => void;
  onAvbryt: () => void;
}

/** Rett eller berik ett svar: samme skjema som kunden, med begrunnelse (påkrevd) og sikkerhet. */
function Rettelse({ reiseId, sporsmalId, instans, onLagret, onAvbryt }: RettelseProps) {
  const { t } = useTranslation();
  const [sporsmal, setSporsmal] = useState<Sporsmal | null>(null);
  const [begrunnelse, setBegrunnelse] = useState('');
  const [sikkerhet, setSikkerhet] = useState('bekreftet');
  const [feil, setFeil] = useState<string | null>(null);
  const [lagrer, setLagrer] = useState(false);
  const id = `rett-${sporsmalId}-${instans}`;

  useEffect(() => {
    konsollApi
      .sporsmal(reiseId, sporsmalId, instans)
      .then(setSporsmal)
      .catch((e) => setFeil(e instanceof ApiFeil ? e.kode : 'ukjent'));
  }, [reiseId, sporsmalId, instans]);

  async function lagre(svar: SvarInn): Promise<boolean> {
    if (!begrunnelse.trim()) {
      setFeil('begrunnelse_mangler');
      return false;
    }
    setLagrer(true);
    setFeil(null);
    try {
      await konsollApi.rett(reiseId, { sporsmalId, instans, verdi: svar.verdi, kundensOrd: svar.kundensOrd, sikkerhet, begrunnelse: begrunnelse.trim() });
      onLagret();
      return true;
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
      return false;
    } finally {
      setLagrer(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h4 className="mb-3 font-semibold">{t('konsoll.rett.tittel')}</h4>
      <div className="mb-4 grid gap-4 sm:grid-cols-[1fr_auto]">
        <div className="flex flex-col">
          <label htmlFor={`${id}-begrunnelse`} className="mb-1.5 text-sm font-medium text-slate-700">
            {t('konsoll.rett.begrunnelse')}
          </label>
          <p id={`${id}-hjelp`} className="mb-1 text-sm text-slate-600">
            {t('konsoll.rett.begrunnelseHjelp')}
          </p>
          <textarea
            id={`${id}-begrunnelse`}
            rows={2}
            value={begrunnelse}
            aria-describedby={`${id}-hjelp`}
            onChange={(e) => setBegrunnelse(e.target.value)}
            className="felt w-full"
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor={`${id}-sikkerhet`} className="mb-1.5 text-sm font-medium text-slate-700">
            {t('konsoll.rett.sikkerhet')}
          </label>
          <select id={`${id}-sikkerhet`} value={sikkerhet} onChange={(e) => setSikkerhet(e.target.value)} className="felt">
            {['bekreftet', 'antatt', 'uavklart'].map((s) => (
              <option key={s} value={s}>
                {t(`konsoll.sikkerhet.${s}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {sporsmal && (
        <SporsmalSkjema
          sporsmal={sporsmal}
          lagrer={lagrer}
          feil={feil}
          kanGaTilbake={false}
          onSvar={lagre}
          onTilbake={onAvbryt}
          konsulent={{ lagreTekst: t('konsoll.rett.lagre') }}
        />
      )}
      {!sporsmal && feil && (
        <p role="alert" className="melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}
      <button type="button" onClick={onAvbryt} className="mt-3 text-sm underline">
        {t('konsoll.rett.avbryt')}
      </button>
    </div>
  );
}
